<template>
    <div>
        <b-row v-if="pendingClaims.length || historicClaims.length">
            <b-col>
                <h5 class="mt-3">
                    Scheduled Claims
                    <HelpPopup>
                        <div>
                            Claims staged against this fill are transmitted overnight once the fill date
                            arrives. They are tried in order, and the sequence stops at the first plan whose
                            response matches what was expected.
                        </div>
                    </HelpPopup>
                </h5>
                <b-table-simple small>
                    <b-thead>
                        <b-tr>
                            <b-th style="width: 4rem"></b-th>
                            <b-th>Plan</b-th>
                            <b-th>Expected</b-th>
                            <b-th>Overrides</b-th>
                            <b-th>Status</b-th>
                            <b-th>Scheduled By</b-th>
                            <b-th v-if="!readOnly"></b-th>
                        </b-tr>
                    </b-thead>
                    <b-tbody>
                        <b-tr v-for="(claim, index) in pendingClaims" :key="`pending-${claim.id}`">
                            <b-td>
                                <b-button-group size="sm">
                                    <b-button :disabled="disabled || index === 0" variant="outline-secondary"
                                        title="Move earlier in the sequence" @click="moveEarlier(index)">
                                        <b-icon-arrow-up />
                                    </b-button>
                                    <b-button :disabled="disabled || index === pendingClaims.length - 1"
                                        variant="outline-secondary" title="Move later in the sequence"
                                        @click="moveLater(index)">
                                        <b-icon-arrow-down />
                                    </b-button>
                                </b-button-group>
                            </b-td>
                            <b-td>{{ planLabel(claim) }}</b-td>
                            <b-td>
                                <span :class="claim.expectsRejection ? 'text-danger' : 'text-success'">
                                    {{ claim.expectsRejection ? 'Rejection' : 'Paid' }}
                                </span>
                                <div v-if="claim.expectedRejectCodeList.length">
                                    <small v-for="code in claim.expectedRejectCodeList" :key="code" class="d-block text-muted">
                                        {{ code }} - {{ rejectReason(code) }}
                                    </small>
                                </div>
                            </b-td>
                            <b-td><small>{{ overrideSummary(claim) }}</small></b-td>
                            <b-td>{{ statusLabel(claim) }}</b-td>
                            <b-td><small>{{ claim.scheduledBy }}</small></b-td>
                            <b-td v-if="!readOnly">
                                <b-button :disabled="disabled" class="mr-2" size="sm" variant="info"
                                    @click="edit(claim)">Edit
                                </b-button>
                                <b-button :disabled="disabled" size="sm" variant="warning"
                                    @click="confirmCancel(claim)">Cancel
                                </b-button>
                            </b-td>
                        </b-tr>
                        <b-tr v-for="claim in historicClaims" :key="`historic-${claim.id}`" class="text-muted">
                            <b-td></b-td>
                            <b-td>{{ planLabel(claim) }}</b-td>
                            <b-td>{{ claim.expectsRejection ? 'Rejection' : 'Paid' }}</b-td>
                            <b-td><small>{{ overrideSummary(claim) }}</small></b-td>
                            <b-td>{{ statusLabel(claim) }}</b-td>
                            <b-td><small>{{ claim.scheduledBy }}</small></b-td>
                            <b-td v-if="!readOnly"></b-td>
                        </b-tr>
                    </b-tbody>
                </b-table-simple>
            </b-col>
        </b-row>

        <b-modal id="schedule-claim-modal" ref="schedule-claim-modal" size="xl" no-close-on-backdrop
            :title="editing && editing.id ? 'Edit Scheduled Claim' : 'Schedule Claim'"
            :ok-disabled="saving || !editing || !editing.patientPlanID" ok-title="Save" @ok="save">
            <template v-if="editing">
                <b-form-row>
                    <b-col cols="6">
                        <b-form-group label="Plan:" label-cols="3" label-for="schedule-claim-plan">
                            <b-form-select id="schedule-claim-plan" v-model="editing.patientPlanID"
                                :options="planOptions" />
                        </b-form-group>
                    </b-col>
                    <b-col cols="6">
                        <!-- pt-0 on the label because the content is a radio group, not an input: the
                             col-form-label top padding that centres a label against an input's box
                             pushes it below the radios instead. -->
                        <b-form-group label="Expected Outcome:" label-cols="4" label-class="pt-0">
                            <b-form-radio-group v-model="editing.expectedOutcome" :options="outcomeOptions" />
                        </b-form-group>
                    </b-col>
                </b-form-row>
                <b-form-row v-if="editing.expectsRejection">
                    <b-col cols="12">
                        <b-form-group label="Expected Reject Codes:" label-cols="2"
                            label-for="schedule-claim-expected-reject-codes"
                            description="Optional. Comma-separated NCPDP reject codes. When set, a rejection only counts as expected if these codes come back; anything else sends the fill to billing review.">
                            <b-form-input id="schedule-claim-expected-reject-codes"
                                v-model="editing.expectedRejectCodes" placeholder="e.g. 70, 75"
                                maxlength="250" />
                            <small v-for="code in editing.expectedRejectCodeList" :key="code" class="d-block text-muted">
                                {{ code }} - {{ rejectReason(code) }}
                            </small>
                        </b-form-group>
                    </b-col>
                </b-form-row>
                <hr />
                <b-form-row>
                    <BillingOverridesForm v-model="editingOverrides" id-prefix="schedule-claim"
                        :reject-code-options="stagedRejectCodeOptions"
                        no-reject-codes-text="No expected reject codes staged on an earlier plan"
                        reject-code-description="Forces which reject code from an earlier plan in this sequence is sent in this claim's COB segment." />
                </b-form-row>
            </template>
        </b-modal>
    </div>
</template>

<script lang="ts">
import { Component, Prop, Ref, Vue, Watch } from 'vue-property-decorator';
import Axios from 'axios';
import { BModal, BvModalEvent } from 'bootstrap-vue';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';
import BillingOverridesForm from '@/components/InsurancePlan/BillingOverridesForm.vue';
import HelpPopup from '@/components/HelpPopup.vue';
import { BillingOverrides } from '@/models/BillingOverrides';
import { CombinedPlan } from '@/models/CombinedPlan';
import { Prescription } from '@/models/Prescription';
import { ClaimRejects } from '@/data/ClaimRejects';
import {
    ScheduledClaim,
    ScheduledClaimExpectedOutcome,
    ScheduledClaimStatus,
} from '@/models/ScheduledClaim';

const STATUS_LABELS: Record<number, string> = {
    [ScheduledClaimStatus.Pending]: 'Pending',
    [ScheduledClaimStatus.Processing]: 'Processing',
    [ScheduledClaimStatus.Completed]: 'Completed',
    [ScheduledClaimStatus.Cancelled]: 'Cancelled',
    [ScheduledClaimStatus.Failed]: 'Failed',
};

/**
 * Staging for the plans a fill should be billed against once its fill date arrives, and the
 * order to try them in.
 *
 * The list is the fill's whole staging history: pending claims stay actionable, while processed
 * and cancelled ones are kept visible but inert, so a technician can see that last night's run
 * already billed a plan rather than wondering why nothing is scheduled any more.
 */
@Component({
    name: "ScheduledClaimPanel",
    components: { BillingOverridesForm, HelpPopup },
})
export default class ScheduledClaimPanel extends Vue {
    @Ref("schedule-claim-modal") readonly modal!: BModal;

    @Prop({ default: null }) private prescription!: Prescription;
    @Prop({ default: () => [] }) private availablePlans!: CombinedPlan[];
    /** Pre-selected when staging a new claim, so the panel opens on the plan the user was looking at. */
    @Prop({ default: null }) private defaultPlan!: CombinedPlan | null;
    @Prop({ default: false, type: Boolean }) private readOnly!: boolean;
    @Prop({ default: false, type: Boolean }) private disabled!: boolean;

    private claims: ScheduledClaim[] = [];
    private editing: ScheduledClaim | null = null;
    private editingOverrides: BillingOverrides = new BillingOverrides();
    private saving = false;

    // noinspection JSUnusedLocalSymbols
    private readonly outcomeOptions = [
        { value: ScheduledClaimExpectedOutcome.Paid, text: 'Paid' },
        { value: ScheduledClaimExpectedOutcome.Rejected, text: 'Rejection' },
    ];

    /** Still actionable, in the order the worker will try them. */
    get pendingClaims(): ScheduledClaim[] {
        return this.claims.filter(c => c.isPending);
    }

    /** Processed, failed, or cancelled — shown for context but no longer editable. */
    get historicClaims(): ScheduledClaim[] {
        return this.claims.filter(c => !c.isPending);
    }

    /**
     * The plans still available to stage. A plan already waiting on this fill is left out, because the
     * sequence bills plans in turn rather than the same plan repeatedly — staging one twice would send
     * the payer a second copy of the claim it just rejected. The claim being edited keeps its own plan
     * in the list, or the select would open with nothing chosen.
     *
     * Filtered on isLive rather than isPending so this matches what the save proc actually refuses: a
     * claim the worker has just leased is Processing, no longer listed as pending, and still about to
     * be transmitted on that plan. Reading it as available offered a plan the save then declined.
     */
    get planOptions(): { value: number, text: string; }[] {
        const alreadyStaged = new Set(this.claims
            .filter(c => c.isLive && c.id !== this.editing?.id)
            .map(c => c.patientPlanID));

        return this.availablePlans
            .map(cp => ({ value: Number(cp.patientPlan.id ?? 0), text: this.planText(cp) }))
            .filter(option => !alreadyStaged.has(option.value));
    }

    /**
     * The reject codes this claim may force into its COB segment. Unlike an immediate bill — which
     * reads them off a rejection that has already come back — nothing has been transmitted yet, so
     * the only codes known in advance are the ones staged as expected on the plans ahead of this
     * one in the sequence.
     */
    get stagedRejectCodeOptions(): { value: string, text: string; }[] {
        if (!this.editing) return [];
        const position = this.editing.sequencePosition > 0
            ? this.editing.sequencePosition
            : Number.MAX_SAFE_INTEGER;

        const codes = new Set<string>();
        this.pendingClaims
            .filter(c => c.id !== this.editing?.id && c.sequencePosition < position)
            .forEach(c => c.expectedRejectCodeList.forEach(code => codes.add(code)));

        return [...codes].sort().map(code => ({ value: code, text: `${code} - ${this.rejectReason(code)}` }));
    }

    @Watch("prescription.rxID", { immediate: true })
    onPrescriptionChanged(): void {
        void this.fetch();
    }

    async fetch(): Promise<void> {
        if (!this.prescription?.hasFullId) {
            this.claims = [];
            return;
        }

        // The fill this request is for, so a response that lands after the user has navigated on
        // cannot write another prescription's staging onto the panel.
        const requestedRxId = this.prescription.rxID;
        const { storeID, rxNumber, rfNumber } = this.prescription;
        try {
            const resp = await Axios.get<ScheduledClaim[]>(`api/ScheduledClaim/${storeID}/${rxNumber}/${rfNumber}`);
            if (this.prescription.rxID !== requestedRxId) return;
            this.claims = resp.data.map(c => new ScheduledClaim(c));
        } catch (err) {
            console.error('Error while getting scheduled claims', { err });
            this.$notification(NotificationOptions.error(err));
        }
    }

    /**
     * Opens the panel on a new claim for the plan the user currently has selected.
     *
     * The prescription is taken from the caller when it has one, because staging is reached through
     * a save that assigns the Rx and refill numbers: the prop still holds the pre-save draft at the
     * moment the save's callback runs, and staging against that would attach the claim to Rx 0.
     */
    public openForNewClaim(saved: Prescription | null = null): void {
        const rx = saved ?? this.prescription;
        const preferred = Number(this.defaultPlan?.patientPlan?.id ?? 0);
        const claim = new ScheduledClaim({
            storeID: rx.storeID,
            rxNumber: rx.rxNumber,
            rfNumber: rx.rfNumber,
            patientPlanID: 0,
        });
        this.editing = claim;
        // Asked of planOptions rather than re-derived, so the modal can never open on a plan its own
        // select does not list. Set after `editing`, which is what planOptions filters against.
        claim.patientPlanID = this.planOptions.some(o => o.value === preferred) ? preferred : 0;
        this.editingOverrides = claim.toOverrides();
        this.dropUnofferedRejectCode();
        this.modal.show();
    }

    edit(claim: ScheduledClaim): void {
        // Edited on a copy so abandoning the modal leaves the listed row as it was persisted.
        const copy = new ScheduledClaim(JSON.parse(JSON.stringify(claim)));
        this.editing = copy;
        this.editingOverrides = copy.toOverrides();
        this.dropUnofferedRejectCode();
        this.modal.show();
    }

    /**
     * Drops a staged reject code the sequence no longer offers.
     *
     * The options are the codes expected of the plans *ahead* of this claim, so moving it earlier can
     * strip the very code it was staged with. The select is disabled when nothing is on offer, which
     * would leave that value invisible and unclearable on screen while applyOverrides still posted it
     * back — a COB override sourced from a plan that now transmits after this one.
     *
     * The billing component guards the equivalent case with clearStaleRejectCodeOverride; this is that
     * guard for the staged path, applied when the modal opens because that is when the sequence the
     * options are read from can have changed underneath a stored claim.
     */
    private dropUnofferedRejectCode(): void {
        const staged = this.editingOverrides.rejectCodeOverride;
        if (staged == null) return;
        if (this.stagedRejectCodeOptions.some(o => o.value === staged)) return;
        this.editingOverrides.rejectCodeOverride = null;
    }

    async save(event: BvModalEvent): Promise<void> {
        if (!this.editing) return;
        event.preventDefault();
        // ok-disabled reflects `saving`, but only from the next render, so two fast clicks both reach
        // here with editing.id still null and would stage the same plan twice. The save proc refuses the
        // duplicate outright; this is what keeps a technician from seeing that error for a double click.
        if (this.saving) return;

        this.saving = true;
        // Inside the try along with the request: reading the form back is where the save is most
        // likely to fail on something other than the server's answer, and a throw out here left the
        // modal open with the data in it and nothing said, which reads as a dead Save button.
        try {
            this.editing.applyOverrides(this.editingOverrides);
            // Codes entered before the outcome was switched back to Paid are not a rejection the
            // claim is waiting for any more, and leaving them staged would have the comparator hold
            // a paid claim to a reject-code match it can never satisfy.
            if (!this.editing.expectsRejection) this.editing.expectedRejectCodes = null;

            await Axios.post<ScheduledClaim>('api/ScheduledClaim', this.editing);
            this.modal.hide();
            this.editing = null;
            await this.fetch();
            this.$emit('changed');
        } catch (err) {
            this.$notification(NotificationOptions.errorSaveNotificationPreset("Scheduled Claim", err));
            console.error('Error while saving a scheduled claim', { err });
        } finally {
            this.saving = false;
        }
    }

    async confirmCancel(claim: ScheduledClaim): Promise<void> {
        const confirmed = await this.$bvModal.msgBoxConfirm(
            `Cancel the scheduled claim for ${this.planLabel(claim)}? It will not be billed.`,
            { centered: true, okVariant: 'warning', okTitle: 'Cancel Claim', cancelTitle: 'Keep' });
        if (!confirmed || claim.id == null) return;

        try {
            await Axios.post(`api/ScheduledClaim/${claim.storeID}/${claim.rxNumber}/${claim.rfNumber}/${claim.id}/Cancel`);
            await this.fetch();
            this.$emit('changed');
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
            console.error('Error while cancelling a scheduled claim', { err });
        }
    }

    moveEarlier(index: number): void {
        void this.swap(this.pendingClaims[index], this.pendingClaims[index - 1]);
    }

    moveLater(index: number): void {
        void this.swap(this.pendingClaims[index], this.pendingClaims[index + 1]);
    }

    /**
     * Order is exchanged server-side in one call rather than by re-saving each claim with a new
     * position: two live claims on a fill may never share a position, so any client-side sequence
     * of saves would have to pass through a state the database refuses.
     */
    private async swap(first: ScheduledClaim, second: ScheduledClaim): Promise<void> {
        if (first?.id == null || second?.id == null) return;

        try {
            const resp = await Axios.post<ScheduledClaim[]>(
                `api/ScheduledClaim/${first.storeID}/${first.rxNumber}/${first.rfNumber}`
                + `/Swap/${first.id}/${second.id}`);
            this.claims = resp.data.map(c => new ScheduledClaim(c));
            this.$emit('changed');
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
            console.error('Error while reordering scheduled claims', { err });
        }
    }

    planLabel(claim: ScheduledClaim): string {
        const plan = this.availablePlans.find(cp => Number(cp.patientPlan.id ?? 0) === claim.patientPlanID);
        return plan ? this.planText(plan) : `Plan ${claim.patientPlanID}`;
    }

    statusLabel(claim: ScheduledClaim): string {
        return STATUS_LABELS[claim.status] ?? 'Unknown';
    }

    rejectReason(code: string): string {
        const trimmed = code.trim();
        return ClaimRejects.codes.find(r => r.Code == trimmed)?.Value
            ?? ClaimRejects.codes.find(r => r.Code == parseInt(trimmed, 10))?.Value
            ?? "Contact IT.";
    }

    /**
     * A one-line digest of what was staged, so the list shows at a glance that a claim carries
     * overrides without having to open each one. Every override the form can set has to appear
     * here, or a claim carrying only the missing ones reads as though it carries none.
     */
    overrideSummary(claim: ScheduledClaim): string {
        const parts: string[] = [];
        if (claim.priorAuthTypeCode) parts.push(`PA Type ${claim.priorAuthTypeCode}`);
        if (claim.priorAuthNumber) parts.push(`PA ${claim.priorAuthNumber}`);
        if (claim.submissionClarificationCode != null) parts.push(`SCC ${claim.submissionClarificationCode}`);
        if (claim.eligibilityClarificationCode != null) parts.push(`ECC ${claim.eligibilityClarificationCode}`);
        if (claim.scheduledPrescriptionIdNumber) parts.push(`TSN ${claim.scheduledPrescriptionIdNumber}`);
        if (claim.usualAndCustomaryOverride != null) parts.push(`U&C ${claim.usualAndCustomaryOverride}`);
        if (claim.incentiveAmountOverride != null) parts.push(`Incentive ${claim.incentiveAmountOverride}`);
        if (claim.daysSupplyOverride != null) parts.push(`Days ${claim.daysSupplyOverride}`);
        if (claim.otherCoverageCodeOverride != null) parts.push(`OCC ${claim.otherCoverageCodeOverride}`);
        if (claim.taxPercentageOverride != null) parts.push(`Tax ${claim.taxPercentageOverride}%`);
        if (claim.icd10) parts.push(`ICD-10 ${claim.icd10}`);
        if (claim.dateOfInjuryOverride) parts.push(`DOI ${claim.dateOfInjuryOverride}`);
        if (claim.rejectCodeOverride) parts.push(`Reject ${claim.rejectCodeOverride}`);
        if (claim.durOverrides.length) parts.push(`${claim.durOverrides.length} DUR`);
        return parts.join(', ') || '—';
    }

    private planText(cp: CombinedPlan): string {
        return cp.plan?.name || cp.patientPlan?.planName || `Plan ${cp.patientPlan?.id ?? ''}`;
    }
}
</script>
